// One Asana project on the import screen: a slat that reads at a glance (name, section, show
// date, the stage it will get, its state, Import), and opens into its fields, team, subtasks and
// what Asana said. Every field shows the Asana value above the Spark control and says where the
// Spark value came from. Only an open line draws its controls: the export holds 250 or more.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert } from '../components/ui/Alert';
import Appear from '../components/ui/Appear';
import { Button, buttonClass } from '../components/ui/Button';
import { Checkbox } from '../components/ui/Checkbox';
import { Chip, ChipGroup } from '../components/ui/Chip';
import { EmptyState } from '../components/ui/EmptyState';
import FadeIn from '../components/ui/FadeIn';
import { Field } from '../components/ui/Field';
import { IconArrowUpRight, IconCheck, IconChevronDown } from '../components/ui/icons';
import { ListRow } from '../components/ui/ListRow';
import LogoSpinner from '../components/ui/LogoSpinner';
import { FOCUS, STATE } from '../components/ui/motion';
import { RadioGroup, RadioRow } from '../components/ui/Radio';
import Reveal from '../components/ui/Reveal';
import SegTrack from '../components/ui/SegTrack';
import { Select } from '../components/ui/Select';
import type { SelectOption } from '../components/ui/Select';
import { Tag } from '../components/ui/Tag';
import Tooltip from '../components/ui/Tooltip';
import { SpiceMeter, StageTag } from '../Spark/SparkBits';
import type { AsanaLine, AsanaSubtask } from '../Spark/sparkApi';
import { CHANNELS, PIPELINE, SPECIALITIES, SPICE, STATUS_REASONS, WORK_STEPS } from '../Spark/sparkRules';
import type { Channel, Speciality, Stage, TeamRole } from '../Spark/sparkRules';
import { dayText } from '../Spark/sparkUi';
import {
  TEAM_ROLES, canTake, drawApplies, needsText, personKey, planTasks,
} from './asanaImportModel';
import type { EffectiveMapping } from './asanaImportModel';
import type {
  AssigneeRole, ImportedAs, LineDraft, LineState, PickPerson, PlannedTask, ResolvedLine, Source, TaskKind,
} from './asanaImportTypes';

/** The slat's columns, shared with the list's column heads. Display is set where it is used. */
const LINE_COLS = 'min-w-0 grid-cols-[minmax(0,1fr)_7rem] items-center gap-x-3 gap-y-1.5 '
  + 'lg:grid-cols-[minmax(0,1fr)_6.5rem_minmax(0,12rem)_minmax(0,15rem)_7rem] '
  + 'xl:grid-cols-[minmax(0,1fr)_minmax(0,11rem)_6.5rem_minmax(0,12rem)_minmax(0,15rem)_7rem]';

const HEAD_CELL = 'text-[11px] font-medium uppercase tracking-wider leading-4 text-on-surface-variant';

/** Column heads over the slats, from `lg` where the columns exist. */
export const LinesHead: React.FC = () => (
  <div aria-hidden="true" className={`${LINE_COLS} hidden px-3 pb-1.5 lg:grid`}>
    <span className={`${HEAD_CELL} pl-[22px]`}>Project</span>
    <span className={`${HEAD_CELL} hidden xl:block`}>Section</span>
    <span className={HEAD_CELL}>Show date</span>
    <span className={HEAD_CELL}>Spark pipeline</span>
    <span className={`${HEAD_CELL} lg:order-last`} />
    <span className={HEAD_CELL}>State</span>
  </div>
);

const LABEL = 'block text-[13px] font-medium leading-[18px] text-on-surface';
const SUBHEAD = 'm-0 text-sm font-semibold leading-5 text-on-surface';

const ROLE_WORD: Record<TeamRole, string> = { producer: 'Producer', designer: 'Designer', pilot: 'Pilot' };
const REFUSED: Record<TeamRole, string> = {
  producer: 'has no producer access',
  designer: 'is not a Designer or Director in the Studio',
  pilot: 'has no pilot access',
};

const STAGE_OPTIONS: SelectOption[] = PIPELINE.map((p) => ({ key: p.key, label: p.label, hex: p.color }));
const NO_REASON = 'none';
const REASON_OPTIONS: SelectOption[] = [
  { key: NO_REASON, label: 'No status' },
  ...STATUS_REASONS.map((s) => ({ key: s.label, label: s.label, hex: s.color })),
];
const NOBODY = 'none';
const KIND_OPTIONS = [
  { key: 'deliverable', label: 'Deliverable' },
  { key: 'subtask', label: 'Subtask' },
  { key: 'out', label: 'Leave out' },
];

const specialityLabel = (s: Speciality) => SPECIALITIES.find((x) => x.key === s)?.label || s;
const tokensText = (line: AsanaLine): string | null => (line.asana.speciality.length ? line.asana.speciality.join(', ') : null);

// ── A field pair ─────────────────────────────────────────────────────────────

interface PairProps {
  label: React.ReactNode;
  /** The control's id, for a native field. Kit selectors and tracks name themselves. */
  htmlFor?: string;
  /** What Asana holds here; null reads "not set". Leave it out where Asana has no such field. */
  asana?: React.ReactNode | null;
  asanaId?: string;
  source: Source;
  /** The words for an inherited value. "From the mapping" by default. */
  inherit?: React.ReactNode;
  /** Replaces the source words: something to pick or fix, in amber. */
  warn?: React.ReactNode;
  /** Replaces the source words: a neutral note. */
  note?: React.ReactNode;
  resetLabel?: string;
  resetTip?: string;
  onReset?: () => void;
  className?: string;
  children: React.ReactNode;
}

/** The Asana value in quiet text, the Spark control under it, and where its value came from. */
const Pair: React.FC<PairProps> = ({
  label, htmlFor, asana, asanaId, source, inherit = 'From the mapping', warn, note, resetLabel, resetTip, onReset, className = '', children,
}) => {
  const words = warn
    ? <span className="text-amber-300">{warn}</span>
    : note
      ? <span className="text-on-surface-variant">{note}</span>
      : source === 'line'
        ? <span className="text-on-surface-variant">Changed here</span>
        : source === 'mapping' || source === 'rule'
          ? <span className="text-on-surface-variant">{inherit}</span>
          : null;
  return (
    <div className={`min-w-0 ${className}`}>
      {htmlFor ? <label htmlFor={htmlFor} className={LABEL}>{label}</label> : <span className={LABEL}>{label}</span>}
      {asana !== undefined && (
        <p id={asanaId} className="m-0 mt-0.5 truncate text-xs leading-4 text-on-surface-variant">
          Asana: {asana ?? 'not set'}
        </p>
      )}
      <div className="mt-1.5">{children}</div>
      <div className="mt-1 flex min-h-7 min-w-0 flex-wrap items-center gap-x-2 text-xs leading-4">
        {words}
        <Appear show={source === 'line' && !!onReset} className="inline-flex">
          <Tooltip label={resetTip || ''}>
            <Button variant="quiet" size="sm" onClick={onReset}>{resetLabel}</Button>
          </Tooltip>
        </Appear>
      </div>
    </div>
  );
};

/** A whole number, zero or more. Typing something else rings the field and is not kept. */
const CountField: React.FC<{ id: string; value: number | null; onChange: (n: number | null) => void; describedBy?: string }> = ({
  id, value, onChange, describedBy,
}) => {
  const [text, setText] = useState(value == null ? '' : String(value));
  const [bad, setBad] = useState(false);
  // A reset from outside (Use Asana's) rewrites the field; a valid keystroke already matches.
  useEffect(() => {
    if ((text.trim() === '' ? null : Number(text)) !== value) {
      setText(value == null ? '' : String(value));
      setBad(false);
    }
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps
  const take = (t: string) => {
    setText(t);
    if (t.trim() === '') { setBad(false); onChange(null); return; }
    const n = Number(t.trim());
    if (Number.isInteger(n) && n >= 0 && n <= 100000) { setBad(false); onChange(n); } else setBad(true);
  };
  return (
    <>
      <Field
        id={id}
        inputMode="numeric"
        value={text}
        error={bad}
        aria-describedby={[describedBy, bad ? `${id}-bad` : ''].filter(Boolean).join(' ') || undefined}
        onChange={(e) => take(e.target.value)}
        onBlur={() => { if (bad) { setText(value == null ? '' : String(value)); setBad(false); } }}
        className="tabular-nums"
      />
      <Reveal show={bad}>
        <p id={`${id}-bad`} className="m-0 pt-1 text-xs text-red-300">A whole number, zero or more. Leaving puts back the last good one.</p>
      </Reveal>
    </>
  );
};

// ── The row ──────────────────────────────────────────────────────────────────

export interface LineRowProps {
  line: AsanaLine;
  r: ResolvedLine;
  state: LineState;
  draft: LineDraft | undefined;
  eff: EffectiveMapping;
  people: PickPerson[];
  peopleById: Map<string, PickPerson>;
  importedAs: ImportedAs | null;
  busy: boolean;
  error: string | null;
  expanded: boolean;
  onToggle: (gid: string) => void;
  onDraft: (gid: string, change: (d: LineDraft) => LineDraft) => void;
  onImport: (gid: string) => void;
  onDismissError: (gid: string) => void;
}

const StateTag: React.FC<{ state: LineState; r: ResolvedLine; fresh: boolean }> = ({ state, r, fresh }) => {
  if (state === 'imported') {
    return (
      <Tag tone="good">
        <IconCheck draw={fresh} className="-ml-0.5 mr-1 inline-block h-3 w-3 align-[-2px]" />
        Imported
      </Tag>
    );
  }
  if (state === 'ready') return <Tag tone="neutral">{r.link_to ? 'Ready to mark' : 'Ready'}</Tag>;
  return <Tag tone="warn">{needsText(r.missing)}</Tag>;
};

const LineRowImpl: React.FC<LineRowProps> = ({
  line, r, state, draft, eff, people, peopleById, importedAs, busy, error, expanded, onToggle, onDraft, onImport, onDismissError,
}) => {
  const gid = line.gid;
  const imported = state === 'imported';
  const fresh = !!importedAs?.fresh;
  const openRef = useRef<HTMLAnchorElement>(null);
  const wasImported = useRef(imported);
  const linkName = r.link_to ? line.same_name.find((p) => p.id === r.link_to)?.name || line.name : '';

  // The Import key leaves as the line lands; focus goes to what took its place, not to the page.
  useEffect(() => {
    if (imported && !wasImported.current) {
      const at = document.activeElement;
      if (!at || at === document.body) openRef.current?.focus();
    }
    wasImported.current = imported;
  }, [imported]);

  const name = (r.name.value || '').trim() || line.name || 'No name';
  const verb = r.link_to ? 'Mark it' : 'Import';
  const tip = state === 'needs'
    ? needsText(r.missing)
    : r.link_to ? `Mark ${linkName} in Spark as this Asana task` : 'Create this project in Spark, with its kept subtasks';
  const tipNote = state === 'needs'
    ? 'Open the project to pick, or set it in the mapping.'
    : r.link_to ? 'Nothing is created.' : undefined;

  const openLink = importedAs?.id ? (
    <Tooltip label={`Open ${importedAs.name} in Spark`}>
      <Link ref={openRef} to={`/spark/projects/${importedAs.id}`} className={`${buttonClass('quiet', 'sm')} -mr-1`}>
        Open in Spark
        <IconArrowUpRight className="h-3.5 w-3.5" />
      </Link>
    </Tooltip>
  ) : <span className="text-xs text-on-surface-variant">In Spark</span>;

  const action = imported ? (fresh ? <FadeIn>{openLink}</FadeIn> : openLink) : (
    <Tooltip label={tip} note={tipNote}>
      <Button
        variant="filled"
        size="sm"
        disabled={state === 'needs'}
        aria-busy={busy || undefined}
        onClick={() => { if (!busy) onImport(gid); }}
      >
        {busy ? <><LogoSpinner size="inline" />{r.link_to ? 'Marking' : 'Importing'}</> : verb}
      </Button>
    </Tooltip>
  );

  const bodyId = `asana-line-${gid}`;
  // An open line keeps its header (and its Import key) in view while its subtasks scroll past.
  const sticky = expanded && !imported
    ? 'sticky top-0 z-[1] -mx-3 -mt-2.5 rounded-t-panel bg-surface-container px-3 pb-2 pt-2.5'
    : '';

  return (
    <ListRow tone={imported ? 'good' : undefined}>
      <div className={`grid ${LINE_COLS} ${sticky}`}>
        <h3 className="m-0 min-w-0 text-sm font-medium leading-5">
          <button
            type="button"
            aria-expanded={expanded}
            aria-controls={expanded ? bodyId : undefined}
            onClick={() => onToggle(gid)}
            className={`-mx-1.5 flex w-full min-w-0 items-center gap-1.5 rounded-control px-1.5 py-1 text-left text-on-surface ${STATE} ${FOCUS}`}
          >
            <IconChevronDown
              className={`h-4 w-4 flex-none text-on-surface-variant transition-transform duration-standard ease-emphasized motion-reduce:transition-none ${expanded ? '' : '-rotate-90'}`}
            />
            <span className="min-w-0 truncate">{name}</span>
          </button>
        </h3>
        <span className="hidden min-w-0 truncate text-[13px] text-on-surface-variant xl:block">{line.section || 'No section'}</span>
        <span className={`hidden text-[13px] tabular-nums lg:block ${r.show_date.value ? 'text-on-surface' : 'text-on-surface-variant'}`}>
          {r.show_date.value ? dayText(r.show_date.value) : 'No date'}
        </span>
        <span className="hidden min-w-0 lg:flex">
          {r.stage.value ? <StageTag stage={r.stage.value} /> : <span className="text-[13px] text-on-surface-variant">Not set</span>}
        </span>
        <span className="flex min-w-0 justify-end lg:order-last">{action}</span>
        <span className="col-span-2 flex min-w-0 lg:col-span-1">
          <StateTag state={state} r={r} fresh={fresh} />
        </span>
      </div>

      <Alert show={!!error} tone="error" variant="note" onDismiss={() => onDismissError(gid)} className="mt-2">
        {error}
      </Alert>

      <Reveal show={expanded}>
        {expanded && (
          <div id={bodyId} className="pt-2">
            {imported
              ? <ImportedBody line={line} importedAs={importedAs} />
              : <LineBody line={line} r={r} draft={draft} eff={eff} people={people} peopleById={peopleById} onDraft={onDraft} />}
          </div>
        )}
      </Reveal>
    </ListRow>
  );
};

/** Rows redraw only when their own line, its draft or the mapping changes. */
export const LineRow = React.memo(LineRowImpl);

// ── An imported line ─────────────────────────────────────────────────────────

const ImportedBody: React.FC<{ line: AsanaLine; importedAs: ImportedAs | null }> = ({ line, importedAs }) => (
  <div className="space-y-5 pb-1">
    <p className="m-0 max-w-[65ch] text-sm text-on-surface">
      {importedAs?.id ? (
        <>
          In Spark as{' '}
          <Link to={`/spark/projects/${importedAs.id}`} className="font-medium text-primary-ink underline-offset-2 hover:underline">
            {importedAs.name}
          </Link>
          . Change anything there; this screen will not import it again.
        </>
      ) : 'This Asana task was already in Spark. Upload the export again to see which project it became.'}
    </p>
    <FromAsana line={line} />
  </div>
);

// ── An open line ─────────────────────────────────────────────────────────────

interface BodyProps {
  line: AsanaLine;
  r: ResolvedLine;
  draft: LineDraft | undefined;
  eff: EffectiveMapping;
  people: PickPerson[];
  peopleById: Map<string, PickPerson>;
  onDraft: (gid: string, change: (d: LineDraft) => LineDraft) => void;
}

const LineBody: React.FC<BodyProps> = ({ line, r, draft, eff, people, peopleById, onDraft }) => {
  const gid = line.gid;
  const id = (k: string) => `asana-${gid}-${k}`;
  const set = <K extends keyof LineDraft>(key: K, value: LineDraft[K]) => onDraft(gid, (d) => ({ ...d, [key]: value }));
  const reset = (key: keyof LineDraft) => onDraft(gid, (d) => {
    const next = { ...d };
    delete next[key];
    return next;
  });
  const setTeam = (role: TeamRole, value: string | null) => onDraft(gid, (d) => ({ ...d, team: { ...(d.team || {}), [role]: value } }));
  const resetTeam = (role: TeamRole) => onDraft(gid, (d) => {
    const team = { ...(d.team || {}) };
    delete team[role];
    return { ...d, team };
  });

  const linking = !!r.link_to;
  const linkName = linking ? line.same_name.find((p) => p.id === r.link_to)?.name || '' : '';
  const isShows = r.speciality.value === 'shows';
  const blankMapping = 'Pick one. The mapping leaves it to each project.';
  const specialityWarn = r.speciality.value
    ? undefined
    : r.specialityClash.length > 1
      ? `Asana names ${r.specialityClash.map(specialityLabel).join(' and ')}. Pick one.`
      : blankMapping;
  const nameText = (r.name.value || '').trim();
  const nameWarn = !nameText
    ? 'Give it a name.'
    : nameText.length > 200 ? `Up to 200 characters. This one has ${nameText.length}.` : undefined;

  const workSteps = r.work_steps.value;
  const toggleStep = (w: string) =>
    set('work_steps', workSteps.includes(w) ? workSteps.filter((x) => x !== w) : [...workSteps, w]);

  return (
    <div className="space-y-6 pb-1">
      {line.same_name.length > 0 && (
        <RadioGroup
          label="Spark already has a project with this name"
          value={r.link_to || 'new'}
          onChange={(v) => (v === 'new' ? reset('link_to') : set('link_to', v))}
          className="max-w-2xl"
        >
          <RadioRow value="new">Import as a new project</RadioRow>
          {line.same_name.map((p) => (
            <RadioRow key={p.id} value={p.id}>
              This is {p.name}{p.show_date ? ` (${dayText(p.show_date)})` : ''} in Spark: mark it, import nothing
            </RadioRow>
          ))}
        </RadioGroup>
      )}

      <Reveal show={linking}>
        <p className="m-0 max-w-[65ch] text-sm text-on-surface-variant">
          Nothing is created. {linkName} is marked as this Asana task, so a later upload shows it as Imported.
          Its own fields and deliverables stay as they are.
        </p>
      </Reveal>

      <Reveal show={!linking}>
        <div className="space-y-6">
          <section aria-label="Project fields" className="space-y-1">
            <div className="grid gap-x-5 gap-y-3 sm:grid-cols-2 xl:grid-cols-4">
              <Pair
                label="Name"
                htmlFor={id('name')}
                asana={line.name || null}
                asanaId={id('name-asana')}
                source={r.name.source}
                warn={nameWarn}
                resetLabel="Use Asana's"
                resetTip="Go back to the name in Asana"
                onReset={() => reset('name')}
                className="sm:col-span-2"
              >
                <Field
                  id={id('name')}
                  value={r.name.value}
                  warn={!!nameWarn}
                  aria-describedby={id('name-asana')}
                  onChange={(e) => set('name', e.target.value)}
                />
              </Pair>
              <Pair
                label="Speciality"
                asana={tokensText(line)}
                source={r.speciality.source}
                warn={specialityWarn}
                resetLabel="Use the mapping"
                resetTip="Go back to what the mapping gives this project"
                onReset={() => reset('speciality')}
              >
                <SegTrack
                  label="Speciality"
                  fill
                  value={r.speciality.value}
                  onChange={(k) => set('speciality', k as Speciality)}
                  options={SPECIALITIES.map((s) => ({ key: s.key, label: s.label }))}
                />
              </Pair>
              <Pair
                label="Channel"
                source={r.channel.source}
                inherit="Follows the speciality"
                warn={r.channel.value ? undefined : 'Pick one, or pick the speciality first.'}
                resetLabel="Follow the speciality"
                resetTip="Let the channel follow the speciality again"
                onReset={() => reset('channel')}
              >
                <SegTrack
                  label="Channel"
                  fill
                  value={r.channel.value}
                  onChange={(k) => set('channel', k as Channel)}
                  options={CHANNELS.map((c) => ({ key: c.key, label: c.label }))}
                />
              </Pair>
            </div>

            <Reveal show={!!r.speciality.value && !isShows}>
              <Pair
                label="Work steps"
                asana={tokensText(line)}
                source={r.work_steps.source}
                note={r.work_steps.source !== 'line' && workSteps.length === 0 ? 'None from the mapping. Optional for an imported project.' : undefined}
                resetLabel="Use the mapping"
                resetTip="Go back to the work steps the mapping gives"
                onReset={() => reset('work_steps')}
              >
                <ChipGroup label="Work steps">
                  {WORK_STEPS.map((w) => (
                    <Chip key={w} pressed={workSteps.includes(w)} onClick={() => toggleStep(w)}>{w}</Chip>
                  ))}
                </ChipGroup>
              </Pair>
            </Reveal>

            <div className="grid gap-x-5 gap-y-3 sm:grid-cols-2 xl:grid-cols-4">
              <Pair
                label="Pipeline"
                asana={line.asana.pipeline}
                asanaId={id('stage-asana')}
                source={r.stage.source}
                warn={r.stage.value ? undefined : blankMapping}
                resetLabel="Use the mapping"
                resetTip="Go back to the pipeline column the mapping gives"
                onReset={() => reset('stage')}
              >
                <Select
                  label="Pipeline"
                  options={STAGE_OPTIONS}
                  value={r.stage.value}
                  placeholder="Pick a pipeline column"
                  aria-describedby={id('stage-asana')}
                  onChange={(k) => set('stage', k as Stage)}
                />
              </Pair>
              <Pair
                label="Status"
                asana={line.asana.status}
                asanaId={id('status-asana')}
                source={r.status_reason.source}
                resetLabel="Use the mapping"
                resetTip="Go back to the status the mapping gives"
                onReset={() => reset('status_reason')}
              >
                <Select
                  label="Status"
                  options={REASON_OPTIONS}
                  value={r.status_reason.value || NO_REASON}
                  aria-describedby={id('status-asana')}
                  onChange={(k) => set('status_reason', k === NO_REASON ? null : k)}
                />
              </Pair>
              <Pair
                label={(
                  <span className="flex items-center gap-2">
                    Spice
                    {r.spice.value ? <SpiceMeter level={r.spice.value} focusable={false} /> : null}
                  </span>
                )}
                asana={line.asana.spice}
                source={r.spice.source}
                warn={r.spice.value ? undefined : blankMapping}
                resetLabel="Use the mapping"
                resetTip="Go back to the spice the mapping gives"
                onReset={() => reset('spice')}
                className="sm:col-span-2"
              >
                <SegTrack
                  label="Spice"
                  fill
                  value={r.spice.value ? String(r.spice.value) : null}
                  onChange={(k) => set('spice', Number(k))}
                  options={SPICE.map((s) => ({ key: String(s.level), label: s.label, hint: s.short }))}
                />
              </Pair>
              <Pair
                label="Show date"
                htmlFor={id('show')}
                asana={line.asana.show_date ? dayText(line.asana.show_date) : null}
                asanaId={id('show-asana')}
                source={r.show_date.source}
                note={r.event_id ? 'The linked event sets it once imported.' : undefined}
                resetLabel="Use Asana's"
                resetTip="Go back to the show date in Asana"
                onReset={() => reset('show_date')}
              >
                <Field
                  id={id('show')}
                  type="date"
                  value={r.show_date.value || ''}
                  aria-describedby={id('show-asana')}
                  onChange={(e) => set('show_date', e.target.value || null)}
                />
              </Pair>
              <Pair
                label="Preview due"
                htmlFor={id('preview')}
                asana={line.asana.delivery_date ? dayText(line.asana.delivery_date) : null}
                asanaId={id('preview-asana')}
                source={r.preview_due.source}
                resetLabel="Use Asana's"
                resetTip="Go back to the delivery date in Asana"
                onReset={() => reset('preview_due')}
              >
                <Field
                  id={id('preview')}
                  type="date"
                  value={r.preview_due.value || ''}
                  aria-describedby={id('preview-asana')}
                  onChange={(e) => set('preview_due', e.target.value || null)}
                />
              </Pair>
              <Pair
                label="Drones"
                htmlFor={id('drones')}
                asana={line.asana.drones}
                asanaId={id('drones-asana')}
                source={r.ls_drone_count.source}
                resetLabel="Use Asana's"
                resetTip="Go back to the drone quantity in Asana"
                onReset={() => reset('ls_drone_count')}
              >
                <CountField
                  id={id('drones')}
                  value={r.ls_drone_count.value}
                  describedBy={id('drones-asana')}
                  onChange={(n) => set('ls_drone_count', n)}
                />
              </Pair>
              <Pair
                label="Pyro drones"
                htmlFor={id('pyro')}
                asana={line.asana.pyro}
                asanaId={id('pyro-asana')}
                source={r.pyro_drone_count.source}
                resetLabel="Use Asana's"
                resetTip="Go back to the pyro drone quantity in Asana"
                onReset={() => reset('pyro_drone_count')}
              >
                <CountField
                  id={id('pyro')}
                  value={r.pyro_drone_count.value}
                  describedBy={id('pyro-asana')}
                  onChange={(n) => set('pyro_drone_count', n)}
                />
              </Pair>
              <Pair
                label="Client"
                htmlFor={id('client')}
                asana={line.asana.client}
                asanaId={id('client-asana')}
                source={r.client_name.source}
                resetLabel="Use Asana's"
                resetTip="Go back to the client in Asana"
                onReset={() => reset('client_name')}
                className="sm:col-span-2"
              >
                <Field
                  id={id('client')}
                  value={r.client_name.value || ''}
                  maxLength={200}
                  aria-describedby={id('client-asana')}
                  onChange={(e) => set('client_name', e.target.value === '' ? null : e.target.value)}
                />
              </Pair>
            </div>
          </section>

          <TeamAndEvent
            line={line}
            r={r}
            people={people}
            peopleById={peopleById}
            onRole={(role) => (role === 'producer' ? reset('assignee_role') : set('assignee_role', role))}
            onTeam={setTeam}
            onResetTeam={resetTeam}
            onEvent={(e) => (e ? set('event_id', e) : reset('event_id'))}
            onDraw={(on) => set('draw', on)}
          />

          <Subtasks line={line} draft={draft} eff={eff} peopleById={peopleById} onDraft={onDraft} />
        </div>
      </Reveal>

      <FromAsana line={line} />
    </div>
  );
};

// ── Team and event ───────────────────────────────────────────────────────────

const personHint = (p: PickPerson): string | undefined => {
  if (!p.active) return 'Deactivated';
  if (!p.inSpark) return 'Cannot open Spark';
  return undefined;
};

const TeamAndEvent: React.FC<{
  line: AsanaLine;
  r: ResolvedLine;
  people: PickPerson[];
  peopleById: Map<string, PickPerson>;
  onRole: (role: AssigneeRole) => void;
  onTeam: (role: TeamRole, userId: string | null) => void;
  onResetTeam: (role: TeamRole) => void;
  onEvent: (eventId: string | null) => void;
  onDraw: (on: boolean) => void;
}> = ({ line, r, people, peopleById, onRole, onTeam, onResetTeam, onEvent, onDraw }) => {
  const assignee = line.assignee ? line.assignee.name || line.assignee.email : null;
  const mapped = r.assigneeUser ? peopleById.get(r.assigneeUser)?.name || 'Someone' : null;
  const drawable = drawApplies(r);
  const date = line.asana.show_date ? dayText(line.asana.show_date) : '';
  const eventOptions: SelectOption[] = [
    { key: NOBODY, label: 'No event' },
    ...line.events.map((e) => ({ key: e.id, label: e.name, hint: [dayText(e.event_date), e.location].filter(Boolean).join(', ') })),
  ];

  return (
    <section aria-labelledby={`asana-${line.gid}-team`} className="space-y-3">
      <h4 id={`asana-${line.gid}-team`} className={SUBHEAD}>Team{line.events.length ? ' and event' : ''}</h4>
      <div className="grid gap-x-5 gap-y-3 sm:grid-cols-2 xl:grid-cols-4">
        {line.assignee ? (
          <Pair
            label="Asana assignee's role"
            asana={assignee}
            source="asana"
            note={mapped ? `${mapped} in Firefleet, through People` : 'Not matched to anyone in Firefleet. Set them in People.'}
          >
            <SegTrack
              label="Asana assignee's role"
              fill
              value={r.assignee_role}
              onChange={(k) => onRole(k as AssigneeRole)}
              options={[
                ...TEAM_ROLES.map((role) => ({ key: role, label: ROLE_WORD[role] })),
                { key: 'none', label: 'Nobody' },
              ]}
            />
          </Pair>
        ) : (
          <div className="min-w-0">
            <span className={LABEL}>The Asana assignee</span>
            <p className="m-0 mt-1.5 text-sm text-on-surface-variant">Nobody is assigned in Asana.</p>
          </div>
        )}
        {TEAM_ROLES.map((role) => {
          const slot = r.team[role];
          const fromAssignee = r.assignee_role === role && !!line.assignee;
          const refusedId = r.ineligible[role];
          const eligible = people.filter((p) => canTake(role, p));
          const current = slot.value && !eligible.some((p) => p.id === slot.value) ? peopleById.get(slot.value) : undefined;
          const options: SelectOption[] = [
            { key: NOBODY, label: 'Nobody' },
            ...(current ? [{ key: current.id, label: current.name, hint: personHint(current) }] : []),
            ...eligible.map((p) => ({ key: p.id, label: p.name })),
          ];
          return (
            <Pair
              key={role}
              label={ROLE_WORD[role]}
              asana={fromAssignee ? assignee : undefined}
              source={slot.source}
              inherit="From the Asana assignee, through People"
              warn={refusedId ? `${peopleById.get(refusedId)?.name || 'They'} ${REFUSED[role]}, so Spark would refuse them here.` : undefined}
              resetLabel="Use the mapping"
              resetTip="Go back to what the Asana assignee gives"
              onReset={() => onResetTeam(role)}
            >
              <Select
                label={ROLE_WORD[role]}
                options={options}
                value={slot.value || NOBODY}
                onChange={(k) => onTeam(role, k === NOBODY ? null : k)}
              />
            </Pair>
          );
        })}
        {line.events.length > 0 && (
          <Pair
            label="Event"
            source="asana"
            note={r.event_id
              ? 'The show date follows the event.'
              : `${line.events.length} Firefleet event${line.events.length === 1 ? '' : 's'} on ${date}, not yet linked`}
          >
            <Select label="Event" options={eventOptions} value={r.event_id || NOBODY} onChange={(k) => onEvent(k === NOBODY ? null : k)} />
          </Pair>
        )}
        <Reveal show={drawable}>
          <div className="pt-5">
            <Checkbox
              label="Draw a team when it imports"
              description="Spark's draw picks a producer, designer and pilot by workload."
              checked={r.draw}
              onChange={(e) => onDraw(e.target.checked)}
            />
          </div>
        </Reveal>
      </div>
    </section>
  );
};

// ── Subtasks ─────────────────────────────────────────────────────────────────

const promotedText = (p: PlannedTask): string | null => {
  if (!p.promoted) return null;
  if (p.promoted.reason === 'nested') return `Goes in as a deliverable: Spark nests one level, and ${p.promoted.under} is a subtask.`;
  return 'Goes in as a deliverable: nothing above it is kept.';
};

const Subtasks: React.FC<{
  line: AsanaLine;
  draft: LineDraft | undefined;
  eff: EffectiveMapping;
  peopleById: Map<string, PickPerson>;
  onDraft: (gid: string, change: (d: LineDraft) => LineDraft) => void;
}> = ({ line, draft, eff, peopleById, onDraft }) => {
  const plan = useMemo(() => planTasks(line, draft, eff), [line, draft, eff]);
  const byGid = useMemo(() => new Map(line.subtasks.map((s) => [s.gid, s])), [line]);
  const kept = plan.filter((p) => p.kind !== 'out').length;
  const anyChosen = plan.some((p) => p.chosen);
  const headId = `asana-${line.gid}-subtasks`;
  const setKind = (taskGid: string, kind: TaskKind) =>
    onDraft(line.gid, (d) => ({ ...d, tasks: { ...(d.tasks || {}), [taskGid]: kind } }));
  const personOf = (taskGid: string): string | null => {
    const key = personKey((byGid.get(taskGid) as AsanaSubtask).assignee);
    const userId = key ? eff.people[key] : null;
    return userId ? peopleById.get(userId)?.name || 'Someone' : null;
  };
  const resetAll = () => onDraft(line.gid, (d) => {
    const next = { ...d };
    delete next.tasks;
    return next;
  });

  return (
    <section aria-labelledby={headId} className="space-y-2">
      <div className="flex min-h-7 flex-wrap items-center gap-x-3 gap-y-1">
        <h4 id={headId} className={SUBHEAD}>Subtasks</h4>
        {plan.length > 0 && <span className="text-xs tabular-nums text-on-surface-variant">{kept} of {plan.length} kept</span>}
        <span className="flex-1" />
        <Appear show={anyChosen} className="inline-flex">
          <Tooltip label="Every subtask goes back to its default: by depth, and the names left out everywhere">
            <Button variant="quiet" size="sm" onClick={resetAll}>Back to the defaults</Button>
          </Tooltip>
        </Appear>
      </div>
      {plan.length === 0 ? (
        <EmptyState variant="line">No subtasks in Asana. Spark starts this project with no deliverables.</EmptyState>
      ) : (
        <ul className="m-0 list-none p-0">
          {plan.map((p) => (
            <SubtaskRow key={p.gid} plan={p} task={byGid.get(p.gid) as AsanaSubtask} person={personOf(p.gid)} onKind={(k) => setKind(p.gid, k)} />
          ))}
        </ul>
      )}
    </section>
  );
};

const SubtaskRow: React.FC<{ plan: PlannedTask; task: AsanaSubtask; person: string | null; onKind: (k: TaskKind) => void }> = ({
  plan, task, person, onKind,
}) => {
  const out = plan.kind === 'out';
  const promoted = promotedText(plan);
  const long = task.name.trim().length > 200;
  return (
    <li className="grid min-w-0 grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 gap-y-1 py-1.5 lg:grid-cols-[minmax(0,1fr)_minmax(0,10rem)_6.5rem_4.5rem_auto]">
      <div className="min-w-0" style={{ paddingLeft: `${Math.max(0, plan.depth - 1) * 1.25}rem` }}>
        <div className={`min-w-0 ${plan.depth > 1 ? 'border-l border-outline-variant pl-2.5' : ''}`}>
          <span className={`block truncate text-sm ${out ? 'text-on-surface-variant' : 'text-on-surface'}`}>{task.name}</span>
          <Reveal show={!!promoted && !out}>
            <span className="block text-xs leading-4 text-amber-300">{promoted}</span>
          </Reveal>
          {long && !out && <span className="block text-xs leading-4 text-amber-300">Spark keeps the first 200 characters of the name.</span>}
        </div>
      </div>
      {/* One row on a phone under the name; its own columns from lg. */}
      <div className="order-last col-span-2 flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1 text-xs lg:contents">
        <span className={`min-w-0 truncate ${person ? 'text-on-surface' : 'text-on-surface-variant'}`}>
          {person || (task.assignee ? 'Not matched' : 'Nobody')}
        </span>
        <span className={`tabular-nums ${task.due_date ? 'text-on-surface' : 'text-on-surface-variant'}`}>
          {task.due_date ? dayText(task.due_date) : 'No date'}
        </span>
        <span className="flex">{task.completed ? <Tag tone="good" size="sm">Done</Tag> : null}</span>
      </div>
      <SegTrack
        size="sm"
        label={`${task.name} goes in as`}
        value={plan.kind}
        onChange={(k) => onKind(k as TaskKind)}
        options={KIND_OPTIONS}
      />
    </li>
  );
};

// ── What Asana said ──────────────────────────────────────────────────────────

const FromAsana: React.FC<{ line: AsanaLine }> = ({ line }) => {
  const a = line.asana;
  const facts: [string, string | null][] = [
    ['Asana task', line.gid],
    ['Section', line.section],
    ['Assignee', line.assignee ? [line.assignee.name, line.assignee.email].filter(Boolean).join(', ') : null],
    ['Created', line.created ? dayText(line.created) : null],
    ['Completed', line.completed ? dayText(line.completed) : null],
    ['Review status', a.review],
    ['Creative process', a.process],
    ['Due date', a.due_date ? dayText(a.due_date) : null],
    ['Client date', a.client_date ? dayText(a.client_date) : null],
    ['Start date', a.start_date ? dayText(a.start_date) : null],
    ['Confirmation', a.confirmation],
  ];
  const shown = facts.filter(([, v]) => v);
  const headId = `asana-${line.gid}-from`;
  return (
    <section aria-labelledby={headId} className="space-y-2">
      <h4 id={headId} className={SUBHEAD}>From Asana</h4>
      <dl className="m-0 grid max-w-4xl grid-cols-[auto_minmax(0,1fr)] gap-x-4 gap-y-1 text-[13px] leading-5 sm:grid-cols-[auto_minmax(0,1fr)_auto_minmax(0,1fr)]">
        {shown.map(([k, v]) => (
          <React.Fragment key={k}>
            <dt className="text-on-surface-variant">{k}</dt>
            <dd className={`m-0 min-w-0 truncate text-on-surface ${k === 'Asana task' ? 'tabular-nums' : ''}`}>{v}</dd>
          </React.Fragment>
        ))}
      </dl>
      {line.notes ? (
        <div
          // A region that scrolls takes focus, so a keyboard can read all of it.
          tabIndex={0}
          role="region"
          aria-label="Notes from Asana"
          className={`max-h-56 max-w-[65ch] overflow-y-auto whitespace-pre-wrap rounded-control pr-2 text-sm leading-5 text-on-surface ${FOCUS}`}
        >
          {line.notes}
        </div>
      ) : (
        <p className="m-0 text-[13px] text-on-surface-variant">No notes in Asana.</p>
      )}
    </section>
  );
};
